import { useCallback } from 'react'
import { ProductOpeningHoursResponse } from '@leid/api-locations'
import { ProductAvailabilityInfo } from '@leid/api-orders'
import { endOfDay, endOfYesterday, isBefore, startOfDay } from 'date-fns'

import { CartFormValues } from 'appTypes/orders'
import { DateTimePickerDatePredicate } from 'components/form/DateTimePicker/interfaces'
import {
  checkIfPeriodStartsEndsInWorkingHours,
  getAvailabilitiesChanges,
  getLocationAvailabilityInfo,
} from 'helpers/utils/availabilities'

export const useShouldDisablePickupDate = (
  isProlonging: boolean,
  values: CartFormValues,
  productLocationAvailability: ProductAvailabilityInfo,
  openingHoursInfo: ProductOpeningHoursResponse[],
): DateTimePickerDatePredicate => {
  return useCallback(
    (date) => {
      if (isProlonging) return true

      if (isBefore(date, endOfYesterday())) {
        return true
      }

      if (!values.selectedLocationId) {
        // No location selected - show all dates as available
        return false
      }

      const locationAvailability = getLocationAvailabilityInfo(values.selectedLocationId, productLocationAvailability)
      if (!locationAvailability) {
        // no info for location — not available
        return true
      }

      const start = startOfDay(date)
      const end = endOfDay(start)

      const availabilitiesChange = getAvailabilitiesChanges(start, end, locationAvailability, false)
      if (availabilitiesChange.every((s) => s < values.amount)) {
        // Date is not available
        return true
      }

      // Check opening hours for start and end of period
      const startsOrEndsInWorkingHours = checkIfPeriodStartsEndsInWorkingHours(
        start,
        end,
        values.selectedLocationId,
        true, // Check only that it's start time
        openingHoursInfo,
        false, // Do not check time, only check that location is not closed
      )
      if (!startsOrEndsInWorkingHours) {
        // It's not working hours or day
        return true
      }

      return false
    },
    [isProlonging, values.selectedLocationId, productLocationAvailability, openingHoursInfo, values.amount],
  )
}
